/**
 * Wires game events to sounds.
 *
 * The store stays the only shared surface: audio subscribes to it exactly the way
 * the HUD does, and never reaches into Babylon or is reached into from it. The
 * mixer arrives as an `AudioSink`, so this whole file is testable with a fake
 * that records what it was asked to play.
 */

import { ruleHides } from '../game/filter';
import type { GameEvent } from '../net/protocol';
import type { GameStore } from '../state/store';
import { CUES, cueFor, SWING_CUES, type CueContext } from './cues';
import { SWING_SAMPLE, SwingWatch, ZoneWatch } from './derived';
import type { AudioSink } from './mixer';

/**
 * Subscribes `sink` to the store's events, and returns a teardown.
 *
 * The listener follows the local player, so attenuation and panning are measured
 * from where the character is rather than where the camera is. They are two
 * different points — the camera trails — and the character is the one being hit.
 *
 * Deliberately does **not** take `store.onExit`: that is a single sink and
 * `main.tsx` already owns it. Leaving for the title has to silence the mixer, so
 * `main.tsx` calls `stopAll()` from its own handler — otherwise every sound
 * mid-envelope keeps ringing over a title screen with no world behind it.
 */
/** How long after arriving in a zone its `down` list is taken as the state found, not as breaks. */
const DOWN_SETTLE_MS = 1200;

export function attachAudio(store: GameStore, sink: AudioSink): () => void {
  const context: CueContext = {
    get selfId() {
      return store.selfId();
    },
    positionOf(id) {
      const e = store.raw.get(id);
      if (e) return { x: e.x, z: e.z };
      // Loot is not an entity; it lives in its own array.
      const item = store.ground().find((g) => g.id === id);
      return item ? { x: item.x, z: item.z } : null;
    },
    skillSoundOf(skillId) {
      return store.skillInfo(skillId)?.sfxCast;
    },
    rarityOf(lootId) {
      return store.ground().find((g) => g.id === lootId)?.rarity ?? null;
    },
    /**
     * The loot filter's opinion about a drop's chime.
     *
     * `hidden` is true only when a **rule** hid it. `evaluateItem`'s built-in Rare+
     * fallback is not a rule, so a player with no filter loaded keeps every drop sound
     * they have today — wiring a filter must not quietly remove something.
     */
    filterOf(lootId) {
      const verdict = store.filterVerdict(lootId);
      if (!verdict) return null;
      return { soundId: verdict.style.soundId, hidden: ruleHides(verdict) };
    },
  };

  const swings = new SwingWatch();
  const zones = new ZoneWatch();
  // Barrels broken since the last snapshot — no event says so; `down` does (`LEVEL-FIXTURES.md`).
  let broken: { seed: number | undefined; down: ReadonlySet<number>; since: number } = {
    seed: undefined,
    down: new Set(),
    since: 0,
  };
  store.onEvent((events: readonly GameEvent[]) => {
    const self = store.selfId();
    const me = self ? store.raw.get(self) : undefined;
    if (me) sink.setListener({ x: me.x, z: me.z });

    for (const event of events) {
      const plan = cueFor(event, context);
      if (plan) sink.play(plan.cue, plan.at, plan.fallback);
    }

    // What no event says, read off the snapshot (`derived.ts`): your own arrival somewhere, and
    // every player's swing, from where they stand.
    if (zones.step(store.zone()?.seed)) sink.play(CUES.zoneChange, null);
    // A smash where each newly broken barrel stood. Not on arriving in a zone whose barrels were
    // already broken: `down` refreshes once a second, so for the first `DOWN_SETTLE_MS` in a zone
    // whatever it says is absorbed silently rather than heard as news.
    const zone = store.zone();
    const down = store.fixturesDown();
    const clock = performance.now();
    if (zone?.seed !== broken.seed) broken = { seed: zone?.seed, down, since: clock };
    if (zone && clock - broken.since >= DOWN_SETTLE_MS) {
      for (const id of down) {
        if (broken.down.has(id)) continue;
        const fixture = zone.fixtures?.find((f) => f.id === id);
        if (fixture?.kind === 'BREAKABLE') sink.play(CUES.impact, { x: fixture.at.x, z: fixture.at.z });
      }
    }
    broken = { ...broken, down };
    for (const swing of swings.step(store.raw.values())) {
      const cue = SWING_CUES[SWING_SAMPLE[swing.weapon]];
      if (cue) sink.play(cue, swing.id === self ? null : swing.at);
    }
  });

  return () => {
    store.onEvent(null);
    sink.stopAll();
  };
}
